import { AudioPreset, MotionData, PerformanceMetrics } from '../types';

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private warmMasterFilter: BiquadFilterNode | null = null;
  private reverbNode: ConvolverNode | null = null;
  private reverbGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  // Background Mindfulness Bed Nodes
  private ambientGain: GainNode | null = null;
  private ambientSources: (AudioNode | OscillatorNode | AudioBufferSourceNode)[] = [];
  private ambientLFO: OscillatorNode | null = null;
  private currentAmbientType: string | null = null;

  public currentPreset: AudioPreset = {
    name: 'Wind Chimes in Mist',
    category: 'Pluck',
    colorHex: '#a5f3fc',
    tuningSystem: '432Hz',
    ambientBed: 'forest-breeze',
    harmonicsType: 'chime',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 432,
    scale: [60, 62, 64, 67, 69, 72, 74, 76],
    attack: 0.08,
    decay: 0.6,
    sustain: 0.2,
    release: 2.8,
    filterQ: 1.2,
    reverbMix: 0.75,
    delayTime: 0.35,
    pitchMod: 0,
    detuneSpread: 2
  };

  private isPlaying: boolean = false;
  private isFadingOut: boolean = false;
  private nextNoteTime: number = 0;
  private activeVoices: { sources: (OscillatorNode | AudioBufferSourceNode)[]; gains: GainNode[] }[] = [];
  
  private destNode: MediaStreamAudioDestinationNode | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];

  // Metrics tracking state
  private isRecordingSession: boolean = false;
  private recordingStartTime: number = 0;
  private recordingIntensitySamples: number[] = [];
  private recordingNoteCount: number = 0;
  private recordingJerkCount: number = 0;
  private recordingNoteTimestamps: number[] = [];

  constructor() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isPlaying) {
          this.resume();
        }
      });
      window.addEventListener('focus', () => {
        if (this.isPlaying) {
          this.resume();
        }
      });
      window.addEventListener('pageshow', () => {
        if (this.isPlaying) {
          this.resume();
        }
      });

      // Global iOS / Safari direct touch unlock listener
      const unlockAudio = () => {
        if (this.ctx) {
          if (this.ctx.state === 'suspended' || (this.ctx.state as string) === 'interrupted') {
            this.ctx.resume().catch(() => {});
          }
          this.unlockSync();
        }
      };
      window.addEventListener('touchstart', unlockAudio, { passive: true, capture: true });
      window.addEventListener('touchend', unlockAudio, { passive: true, capture: true });
      window.addEventListener('click', unlockAudio, { passive: true, capture: true });
    }
  }

  public async init() {
    try {
      if (this.ctx) {
        if (this.ctx.state === 'suspended' || (this.ctx.state as string) === 'interrupted') {
          await this.ctx.resume();
        }
        this.unlockSync();
        return;
      }
      
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.error("Web Audio API not supported");
        return;
      }

      this.ctx = new AudioContextClass();
      
      // Immediately play silent buffer synchronously to unlock iOS Safari Web Audio
      this.unlockSync();

      // Master dynamics compressor with gentle knee for soft limiter action
      this.compressor = this.ctx!.createDynamicsCompressor();
      this.compressor.threshold.setValueAtTime(-26, this.ctx!.currentTime);
      this.compressor.knee.setValueAtTime(32, this.ctx!.currentTime);
      this.compressor.ratio.setValueAtTime(6, this.ctx!.currentTime);
      this.compressor.attack.setValueAtTime(0.015, this.ctx!.currentTime);
      this.compressor.release.setValueAtTime(0.35, this.ctx!.currentTime);

      // Lowered master volume for warm, non-fatiguing playback
      this.masterGain = this.ctx!.createGain();
      this.masterGain.gain.value = 0.35;
      
      // Dynamic expressive lowpass filter
      this.filter = this.ctx!.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = 850;
      this.filter.Q.value = 1.0;

      // Dedicated second-stage warm master lowpass filter (high-cut at 1500Hz)
      // Eliminates vocal formant peaks, piercing sibilance, and high-frequency digital harshness
      this.warmMasterFilter = this.ctx!.createBiquadFilter();
      this.warmMasterFilter.type = 'lowpass';
      this.warmMasterFilter.frequency.value = 1500;
      this.warmMasterFilter.Q.value = 0.707; // Smooth Butterworth curve
      
      // Warm long convolution reverb
      this.reverbNode = this.ctx!.createConvolver();
      this.reverbGain = this.ctx!.createGain();
      this.reverbGain.gain.value = 0.55;

      // Ambient background bed bus
      this.ambientGain = this.ctx!.createGain();
      this.ambientGain.gain.value = 0.13; // Soft, soothing background level

      // Connections: Melodic Voices -> Dynamic Filter -> Warm Master Lowpass Filter -> Master & Reverb
      this.filter.connect(this.warmMasterFilter);
      this.warmMasterFilter.connect(this.masterGain);
      this.warmMasterFilter.connect(this.reverbNode);
      
      this.reverbNode.connect(this.reverbGain);
      this.reverbGain.connect(this.masterGain);
      
      // Ambient bed connects to warm master filter for unified soothing coloration
      this.ambientGain.connect(this.warmMasterFilter);

      this.masterGain.connect(this.compressor);
      this.compressor.connect(this.ctx!.destination);

      // Media Recording Destination
      this.destNode = this.ctx!.createMediaStreamDestination();
      this.compressor.connect(this.destNode);

      // Noise Buffer for organic ambient textures
      this.generateNoiseBuffer();

      this.isPlaying = true;
      await this.loadImpulseResponse();

      // Start current preset's ambient bed
      this.startAmbientBed(this.currentPreset.ambientBed || 'forest-breeze');
    } catch (e) {
      console.error("AudioEngine init error:", e);
    }
  }

  public unlockSync() {
    if (!this.ctx) return;
    try {
      const buffer = this.ctx.createBuffer(1, 1, 22050);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ctx.destination);
      source.start(0);
      if (this.ctx.state === 'suspended' || (this.ctx.state as string) === 'interrupted') {
        this.ctx.resume().catch(() => {});
      }
    } catch (e) {
      console.warn("Audio unlockSync error:", e);
    }
  }

  private generateNoiseBuffer() {
    if (!this.ctx) return;
    const sampleRate = this.ctx.sampleRate;
    const bufferSize = sampleRate * 4;
    this.noiseBuffer = this.ctx.createBuffer(2, bufferSize, sampleRate);
    
    // Generate organic pink/brown noise for natural mountain wind & ocean textures
    for (let channel = 0; channel < 2; channel++) {
      const data = this.noiseBuffer.getChannelData(channel);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.09;
        b6 = white * 0.115926;
      }
    }
  }

  private async unlock() {
    if (!this.ctx) return;
    const buffer = this.ctx.createBuffer(1, 1, 22050);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    source.start(0);
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch (e) {}
    }
  }

  private async loadImpulseResponse() {
    if (!this.ctx || !this.reverbNode) return;
    const sampleRate = this.ctx.sampleRate;
    const length = sampleRate * 3.8; // Deep, cathedral-like warm decay
    const impulse = this.ctx.createBuffer(2, length, sampleRate);
    
    for (let c = 0; c < 2; c++) {
      const data = impulse.getChannelData(c);
      let lastVal = 0;
      for (let i = 0; i < length; i++) {
        // Natural exponential decay with lowpass smoothing to eliminate metallic splash
        const decay = Math.exp(-i / (sampleRate * 0.95));
        const white = (Math.random() * 2 - 1) * decay;
        // Warm 1-pole lowpass filter on impulse response
        lastVal = lastVal * 0.7 + white * 0.3;
        data[i] = lastVal;
      }
    }
    this.reverbNode.buffer = impulse;
  }

  /**
   * Generates continuous organic ambient beddings (ocean, singing bowl drone, binaural theta, forest breeze)
   */
  private startAmbientBed(type?: 'ocean' | 'singing-bowl' | 'forest-breeze' | 'binaural-theta' | 'warm-drone' | 'none') {
    if (!this.ctx || !this.ambientGain) return;
    if (!type || type === 'none') {
      this.stopAmbientBed();
      return;
    }

    if (this.currentAmbientType === type && this.ambientSources.length > 0) return;

    this.stopAmbientBed();
    this.currentAmbientType = type;

    const now = this.ctx.currentTime;
    this.ambientGain.gain.cancelScheduledValues(now);
    this.ambientGain.gain.setValueAtTime(0.001, now);
    this.ambientGain.gain.linearRampToValueAtTime(0.13, now + 2.5);

    try {
      if (type === 'singing-bowl' || type === 'warm-drone') {
        // Multi-frequency warm singing bowl bed (fundamental + warm 5th + sub-octave)
        const fundamental = this.getTunedFrequency(48); // Deep Low C / ground
        const droneFrequencies = [fundamental, fundamental * 1.5, fundamental * 0.5];

        droneFrequencies.forEach((freq, idx) => {
          if (!this.ctx || !this.ambientGain) return;
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();

          osc.type = 'sine'; // Pure warm sine waves
          osc.frequency.setValueAtTime(freq, now);

          // Gentle LFO pitch drift
          const lfo = this.ctx.createOscillator();
          const lfoGain = this.ctx.createGain();
          lfo.frequency.setValueAtTime(0.06 + idx * 0.02, now);
          lfoGain.gain.setValueAtTime(0.8, now);
          lfo.connect(lfoGain);
          lfoGain.connect(osc.frequency);
          lfo.start(now);
          this.ambientSources.push(lfo);

          const targetLevel = (0.16 / (idx + 1));
          gain.gain.setValueAtTime(targetLevel, now);
          osc.connect(gain);
          gain.connect(this.ambientGain);
          osc.start(now);
          this.ambientSources.push(osc, gain);
        });
      } else if (type === 'binaural-theta') {
        // Binaural beat: 136.1 Hz left, 142.1 Hz right -> 6.0 Hz Theta wave for deep calming
        const baseFreq = 136.1;
        const thetaDelta = 6.0;

        const merger = this.ctx.createChannelMerger(2);
        
        const oscL = this.ctx.createOscillator();
        oscL.type = 'sine';
        oscL.frequency.setValueAtTime(baseFreq, now);

        const oscR = this.ctx.createOscillator();
        oscR.type = 'sine';
        oscR.frequency.setValueAtTime(baseFreq + thetaDelta, now);

        const gainL = this.ctx.createGain();
        gainL.gain.setValueAtTime(0.16, now);
        const gainR = this.ctx.createGain();
        gainR.gain.setValueAtTime(0.16, now);

        oscL.connect(gainL);
        gainL.connect(merger, 0, 0);

        oscR.connect(gainR);
        gainR.connect(merger, 0, 1);

        merger.connect(this.ambientGain);

        oscL.start(now);
        oscR.start(now);
        this.ambientSources.push(oscL, oscR, gainL, gainR, merger);
      } else if (type === 'ocean' || type === 'forest-breeze') {
        // Organic noise modulated by slow rhythmic breathing tides
        if (this.noiseBuffer) {
          const noise = this.ctx.createBufferSource();
          noise.buffer = this.noiseBuffer;
          noise.loop = true;

          const noiseFilter = this.ctx.createBiquadFilter();
          noiseFilter.type = 'lowpass';
          noiseFilter.frequency.setValueAtTime(type === 'ocean' ? 280 : 550, now);
          noiseFilter.Q.setValueAtTime(1.0, now);

          // Tidal swell LFO (6-8 second breath cycle)
          const swellLfo = this.ctx.createOscillator();
          const swellGain = this.ctx.createGain();
          swellLfo.frequency.setValueAtTime(0.10, now);
          swellGain.gain.setValueAtTime(type === 'ocean' ? 180 : 120, now);

          swellLfo.connect(swellGain);
          swellGain.connect(noiseFilter.frequency);
          swellLfo.start(now);

          const oceanGain = this.ctx.createGain();
          oceanGain.gain.setValueAtTime(0.12, now);

          noise.connect(noiseFilter);
          noiseFilter.connect(oceanGain);
          oceanGain.connect(this.ambientGain);

          noise.start(now);
          this.ambientSources.push(noise, noiseFilter, swellLfo, swellGain, oceanGain);
        }
      }
    } catch (err) {
      console.warn("Ambient bed creation notice:", err);
    }
  }

  private stopAmbientBed() {
    if (!this.ctx || !this.ambientGain) return;
    const now = this.ctx.currentTime;
    try {
      this.ambientGain.gain.cancelScheduledValues(now);
      this.ambientGain.gain.setValueAtTime(this.ambientGain.gain.value, now);
      this.ambientGain.gain.linearRampToValueAtTime(0.0001, now + 1.2);
    } catch (e) {}

    setTimeout(() => {
      this.ambientSources.forEach(node => {
        try {
          if ('stop' in node && typeof (node as any).stop === 'function') {
            (node as any).stop();
          }
          if ('disconnect' in node && typeof (node as any).disconnect === 'function') {
            (node as any).disconnect();
          }
        } catch (e) {}
      });
      this.ambientSources = [];
      this.currentAmbientType = null;
    }, 1300);
  }

  public async resume() {
    this.isFadingOut = false;
    this.isPlaying = true;
    if (!this.ctx) {
      await this.init();
      return;
    }
    if (this.ctx.state === 'suspended' || (this.ctx.state as string) === 'interrupted') {
      try {
        await this.ctx.resume();
      } catch (e) {
        console.warn('AudioContext resume error:', e);
      }
    }
    this.resetMasterVolume(0.35);
    if (this.currentPreset.ambientBed && this.ambientSources.length === 0) {
      this.startAmbientBed(this.currentPreset.ambientBed);
    }
  }

  public resetMasterVolume(targetVolume: number = 0.35) {
    this.isFadingOut = false;
    this.isPlaying = true;
    if (!this.ctx || !this.masterGain) return;
    const now = this.ctx.currentTime;
    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(Math.max(0.0001, this.masterGain.gain.value), now);
      this.masterGain.gain.linearRampToValueAtTime(targetVolume, now + 0.2);
    } catch (e) {
      this.masterGain.gain.value = targetVolume;
    }
  }

  public async fadeIn(targetVolume: number = 0.35, durationSeconds: number = 2.0): Promise<void> {
    this.isFadingOut = false;
    this.isPlaying = true;
    if (!this.ctx) await this.init();
    if (!this.ctx || !this.masterGain) return;
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch (e) {}
    }
    const now = this.ctx.currentTime;
    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(0.0001, now);
      this.masterGain.gain.linearRampToValueAtTime(targetVolume, now + durationSeconds);
    } catch (e) {
      this.masterGain.gain.value = targetVolume;
    }
  }

  /**
   * Plays a warm, resonant Tibetan singing bowl chime with mellow low-harmonic partials.
   */
  public playSingingBowlChime(fundamentalFreq: number = 216) {
    if (!this.ctx || !this.filter) return;
    const now = this.ctx.currentTime;
    const partials = [
      { ratio: 1.0, gain: 0.25, decay: 5.5 },
      { ratio: 1.5, gain: 0.12, decay: 4.8 },
      { ratio: 2.0, gain: 0.06, decay: 3.5 },
      { ratio: 0.5, gain: 0.15, decay: 5.8 }
    ];

    partials.forEach(({ ratio, gain, decay }) => {
      if (!this.ctx || !this.filter) return;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(fundamentalFreq * ratio, now);
      osc.frequency.exponentialRampToValueAtTime(fundamentalFreq * ratio * 0.998, now + decay);

      g.gain.setValueAtTime(0.0001, now);
      g.gain.linearRampToValueAtTime(gain, now + 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, now + decay);

      osc.connect(g);
      g.connect(this.filter);

      osc.start(now);
      osc.stop(now + decay + 0.1);
    });
  }

  public async fadeOut(durationSeconds: number = 2.5): Promise<void> {
    this.isFadingOut = true;
    if (!this.ctx || !this.masterGain) {
      this.isPlaying = false;
      return;
    }

    const now = this.ctx.currentTime;
    const currentGain = Math.max(0.001, this.masterGain.gain.value);

    try {
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(currentGain, now);
      this.masterGain.gain.exponentialRampToValueAtTime(0.0001, now + durationSeconds);
    } catch (e) {
      try {
        this.masterGain.gain.linearRampToValueAtTime(0.0001, now + durationSeconds);
      } catch (err) {
        this.masterGain.gain.value = 0.0001;
      }
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        this.activeVoices.forEach(v => {
          v.sources.forEach(s => { try { s.stop(); } catch(e){} });
        });
        this.activeVoices = [];
        this.isPlaying = false;
        resolve();
      }, durationSeconds * 1000);
    });
  }

  public getIsFadingOut(): boolean {
    return this.isFadingOut;
  }

  public suspend() {
    this.isPlaying = false;
    this.stopAmbientBed();
    this.activeVoices.forEach(v => {
      v.sources.forEach(s => { try { s.stop(); } catch(e){} });
    });
    this.activeVoices = [];
    if (this.ctx?.state === 'running') {
      try {
        this.ctx.suspend();
      } catch (e) {}
    }
  }

  public setPreset(preset: AudioPreset) {
    const safeScale = Array.isArray(preset.scale) && preset.scale.length > 0
      ? preset.scale.filter(n => typeof n === 'number' && !isNaN(n) && n >= 20 && n <= 127)
      : [48, 52, 55, 60, 62, 64, 67];

    this.currentPreset = {
      ...preset,
      scale: safeScale.length > 0 ? safeScale : [48, 52, 55, 60, 62, 64, 67],
      attack: Math.max(0.04, Math.min(4.0, preset.attack ?? 0.4)),
      decay: Math.max(0.08, Math.min(4.0, preset.decay ?? 0.8)),
      sustain: Math.max(0.01, Math.min(1.0, preset.sustain ?? 0.6)),
      release: Math.max(0.2, Math.min(6.0, preset.release ?? 2.4)),
      filterQ: Math.max(0.1, Math.min(3.0, preset.filterQ ?? 1.0)),
      reverbMix: Math.max(0.2, Math.min(1, preset.reverbMix ?? 0.7)),
      delayTime: Math.max(0, Math.min(1, preset.delayTime ?? 0.35)),
    };

    if (this.filter && this.ctx) {
      // Default to warm lowpass filter regardless of extreme preset settings
      this.filter.type = preset.filterType === 'bandpass' ? 'bandpass' : 'lowpass';
      this.filter.Q.setTargetAtTime(Math.min(2.0, this.currentPreset.filterQ), this.ctx.currentTime, 0.1);
      if (this.reverbGain) {
        this.reverbGain.gain.setTargetAtTime(this.currentPreset.reverbMix, this.ctx.currentTime, 0.5);
      }
    }

    // Switch or update ambient background bed
    if (preset.ambientBed) {
      this.startAmbientBed(preset.ambientBed);
    } else {
      this.stopAmbientBed();
    }
  }

  /**
   * Computes precise 432Hz or 528Hz tuned frequency from MIDI note
   */
  private getTunedFrequency(midiNote: number): number {
    const tuning = this.currentPreset.tuningSystem || '432Hz';
    let a4 = 432;
    if (tuning === '528Hz') a4 = 444;
    else if (tuning === '440Hz') a4 = 440;
    else if (tuning === '417Hz') a4 = 417 * (440/415.3);

    return a4 * Math.pow(2, (midiNote - 69) / 12);
  }

  public async playPreview() {
    if (!this.ctx) await this.init();
    if (this.ctx?.state === 'suspended') {
      try { await this.ctx.resume(); } catch(e) {}
    }
    if (!this.ctx || !this.masterGain || !this.filter) return;

    const scale = this.currentPreset.scale || [48, 52, 55, 60, 64];
    const notesToPlay = scale.slice(0, 4);

    notesToPlay.forEach((midiNote, idx) => {
      setTimeout(() => {
        if (!this.ctx || !this.filter) return;
        this.playSynthesizedNote(midiNote, 0.25, false);
      }, idx * 280);
    });
  }

  public update(motion: MotionData) {
    if (!this.ctx || !this.isPlaying || !this.filter || !this.masterGain || this.isFadingOut) return;
    
    if (this.ctx.state === 'suspended') {
      try { this.ctx.resume(); } catch(e) {}
    }

    if (this.isRecordingSession) {
      if (typeof motion.intensity === 'number') {
        this.recordingIntensitySamples.push(motion.intensity);
      }
      if (motion.jerk && motion.jerk > 1.4) {
        this.recordingJerkCount++;
      }
    }

    const now = this.ctx.currentTime;
    const tiltNorm = Math.max(-1, Math.min(1, motion.rotBeta / 90)); 
    
    // Warm lowpass filter opening gently with movement, strictly capped to prevent high frequency shrillness
    const cutoffFreq = Math.max(220, Math.min(1450, 360 + (Math.abs(tiltNorm) * 900)));
    this.filter.frequency.setTargetAtTime(cutoffFreq, now, 0.12);

    // Mellow dynamic master volume response
    const targetVol = 0.24 + (Math.min(motion.intensity || 0, 1) * 0.22);
    this.masterGain.gain.setTargetAtTime(targetVol, now, 0.1);

    // Meditative spacing: slower rhythmic note intervals that allow sounds to bloom peacefully
    const baseInterval = Math.max(0.48, (this.currentPreset.attack + 0.25));
    const speedMultiplier = 1 - (Math.min(motion.intensity || 0, 0.8) * 0.35);
    const currentInterval = Math.max(0.28, baseInterval * speedMultiplier);

    if (this.nextNoteTime < now - 1.0) {
      this.nextNoteTime = now;
    }

    if (now >= this.nextNoteTime) {
      this.triggerVoice(motion);
      this.nextNoteTime = now + currentInterval;
    }
  }

  private triggerVoice(motion: MotionData, isJerk: boolean = false) {
    if (!this.ctx || !this.masterGain || !this.filter) return;

    if (this.isRecordingSession) {
      this.recordingNoteCount++;
      this.recordingNoteTimestamps.push(performance.now());
    }

    const scale = this.currentPreset.scale || [48, 52, 55, 60, 64];
    const gammaNorm = (motion.rotGamma + 90) / 180;
    const noteIndex = Math.max(0, Math.min(scale.length - 1, Math.floor(gammaNorm * scale.length)));
    const midiNote = scale[noteIndex];

    const intensity = Math.min(0.8, Math.max(0.15, motion.intensity || 0.35));
    this.playSynthesizedNote(midiNote, intensity, isJerk);
  }

  /**
   * Warm, soothing multi-harmonic synthesizer voice generation without vocal formants or high-frequency peaks
   */
  private playSynthesizedNote(midiNote: number, intensity: number = 0.35, isJerk: boolean = false) {
    if (!this.ctx || !this.filter) return;

    const now = this.ctx.currentTime;
    const baseFreq = this.getTunedFrequency(midiNote);
    const harmonicsType = this.currentPreset.harmonicsType || 'warm-analog';
    const detune = this.currentPreset.detuneSpread || 2;

    const atk = Math.max(0.06, isJerk ? 0.04 : this.currentPreset.attack);
    const dec = Math.max(0.1, isJerk ? 0.3 : this.currentPreset.decay);
    const rel = Math.max(0.4, this.currentPreset.release);
    const sus = Math.max(0.01, Math.min(0.85, this.currentPreset.sustain ?? 0.55));

    const sources: (OscillatorNode | AudioBufferSourceNode)[] = [];
    const gains: GainNode[] = [];

    if (this.currentPreset.oscillatorType === 'noise' && this.noiseBuffer) {
      const source = this.ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      source.playbackRate.value = Math.pow(2, (midiNote - 60) / 60);

      const gain = this.ctx.createGain();
      const peak = 0.22 * intensity;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(peak, now + atk);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.001, peak * sus), now + atk + dec);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + atk + dec + rel);

      source.connect(gain);
      gain.connect(this.filter);
      source.start(now);
      source.stop(now + atk + dec + rel + 0.1);

      sources.push(source);
      gains.push(gain);
    } else {
      // Warm, purely harmonic sine & soft triangle synthesis (no harsh formant/metallic peaks)
      const partials = harmonicsType === 'chime'
        ? [
            { ratio: 1.0, detune: -detune, gainFactor: 0.75, wave: 'sine' as OscillatorType },
            { ratio: 1.0, detune: detune, gainFactor: 0.65, wave: 'sine' as OscillatorType },
            { ratio: 0.5, detune: 0, gainFactor: 0.35, wave: 'sine' as OscillatorType }, // Grounding warm sub-octave
            { ratio: 1.5, detune: 2, gainFactor: 0.12, wave: 'sine' as OscillatorType }  // Warm harmonic fifth
          ]
        : harmonicsType === 'bell' || harmonicsType === 'crystal'
        ? [
            { ratio: 1.0, detune: -detune, gainFactor: 0.75, wave: 'sine' as OscillatorType },
            { ratio: 1.0, detune: detune, gainFactor: 0.65, wave: 'sine' as OscillatorType },
            { ratio: 0.5, detune: 0, gainFactor: 0.35, wave: 'sine' as OscillatorType },
            { ratio: 2.0, detune: 0, gainFactor: 0.10, wave: 'sine' as OscillatorType }
          ]
        : harmonicsType === 'bamboo'
        ? [
            { ratio: 1.0, detune: 0, gainFactor: 0.8, wave: 'sine' as OscillatorType },
            { ratio: 0.5, detune: 0, gainFactor: 0.35, wave: 'sine' as OscillatorType },
            { ratio: 2.0, detune: 2, gainFactor: 0.15, wave: 'triangle' as OscillatorType }
          ]
        : [
            // Warm analog sine/triangle blend with grounding sub-oscillator
            { ratio: 1.0, detune: -detune, gainFactor: 0.65, wave: 'sine' as OscillatorType },
            { ratio: 1.0, detune: detune, gainFactor: 0.65, wave: 'triangle' as OscillatorType },
            { ratio: 0.5, detune: 0, gainFactor: 0.40, wave: 'sine' as OscillatorType }
          ];

      partials.forEach(({ ratio, detune: cents, gainFactor, wave }) => {
        if (!this.ctx || !this.filter) return;
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();

        osc.type = wave;
        const targetFreq = baseFreq * ratio;
        osc.frequency.setValueAtTime(targetFreq, now);
        osc.detune.setValueAtTime(cents, now);

        // Lower, warm peak amplitude
        const peak = Math.max(0.015, 0.22 * gainFactor * (isJerk ? 1.2 : 1.0) * intensity);
        g.gain.setValueAtTime(0.0001, now);
        g.gain.linearRampToValueAtTime(peak, now + atk);
        g.gain.exponentialRampToValueAtTime(Math.max(0.001, peak * sus), now + atk + dec);
        g.gain.exponentialRampToValueAtTime(0.0001, now + atk + dec + rel);

        osc.connect(g);
        g.connect(this.filter);
        osc.start(now);
        osc.stop(now + atk + dec + rel + 0.1);

        sources.push(osc);
        gains.push(g);
      });
    }

    const voice = { sources, gains };
    this.activeVoices.push(voice);
    setTimeout(() => {
      const idx = this.activeVoices.indexOf(voice);
      if (idx > -1) this.activeVoices.splice(idx, 1);
    }, (atk + dec + rel + 0.5) * 1000);
  }

  public startRecording() {
    if (!this.destNode) return;
    this.recordedChunks = [];
    this.isRecordingSession = true;
    this.recordingStartTime = performance.now();
    this.recordingIntensitySamples = [];
    this.recordingNoteCount = 0;
    this.recordingJerkCount = 0;
    this.recordingNoteTimestamps = [];

    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    this.mediaRecorder = new MediaRecorder(this.destNode.stream, { mimeType });
    this.mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) this.recordedChunks.push(e.data); };
    this.mediaRecorder.start();
  }

  public async stopRecording(): Promise<{ blob: Blob, url: string, mimeType: string, metrics: PerformanceMetrics } | null> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder) return resolve(null);

      const elapsed = Math.max(1, (performance.now() - this.recordingStartTime) / 1000);
      this.isRecordingSession = false;

      const samples = this.recordingIntensitySamples;
      const avgIntensity = samples.length > 0
        ? samples.reduce((acc, v) => acc + v, 0) / samples.length
        : 0.35;
      const peakIntensity = samples.length > 0
        ? Math.max(...samples)
        : 0.65;

      let tempoStability = 80;
      if (this.recordingNoteTimestamps.length > 2) {
        const intervals: number[] = [];
        for (let i = 1; i < this.recordingNoteTimestamps.length; i++) {
          intervals.push(this.recordingNoteTimestamps[i] - this.recordingNoteTimestamps[i - 1]);
        }
        const meanInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        const variance = intervals.reduce((a, b) => a + Math.pow(b - meanInterval, 2), 0) / intervals.length;
        const stdDev = Math.sqrt(variance);
        const coeffOfVariation = meanInterval > 0 ? stdDev / meanInterval : 0.5;
        tempoStability = Math.round(Math.max(30, Math.min(98, 100 - (coeffOfVariation * 60))));
      }

      const jerkPenalty = Math.min(35, this.recordingJerkCount * 4);
      const smoothnessScore = Math.round(Math.max(40, Math.min(99, 95 - jerkPenalty)));

      const metrics: PerformanceMetrics = {
        durationSeconds: elapsed,
        presetName: this.currentPreset.name,
        presetCategory: this.currentPreset.category || 'Mindfulness',
        averageIntensity: Math.min(1, Math.max(0, avgIntensity)),
        peakIntensity: Math.min(1, Math.max(0, peakIntensity)),
        tempoStability,
        smoothnessScore,
        totalNotesTriggered: this.recordingNoteCount,
        jerkCount: this.recordingJerkCount
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: this.mediaRecorder?.mimeType });
        resolve({
          blob,
          url: URL.createObjectURL(blob),
          mimeType: this.mediaRecorder?.mimeType || 'audio/webm',
          metrics
        });
      };
      this.mediaRecorder.stop();
    });
  }

  public async convertToWav(sourceBlob: Blob): Promise<Blob> {
    return sourceBlob; 
  }
}

export const audioEngine = new AudioEngine();
